import {
  createContext,
  useContext,
  useEffect,
  useId,
  useRef,
  useState,
  type ButtonHTMLAttributes,
  type HTMLAttributes,
  type ReactNode,
  type RefObject,
} from 'react';
import { createPortal } from 'react-dom';
import { cn } from './cn';
import { useAdminPortalElement } from './portal';

type MenuContextValue = {
  open: boolean;
  setOpen: (open: boolean) => void;
  triggerRef: RefObject<HTMLButtonElement | null>;
  menuId: string;
};

const MenuContext = createContext<MenuContextValue | null>(null);

function useMenu() {
  const ctx = useContext(MenuContext);
  if (!ctx) throw new Error('Dropdown menu parts must be used within DropdownMenu');
  return ctx;
}

export function DropdownMenu({ children }: { children: ReactNode }) {
  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const menuId = useId();
  return (
    <MenuContext.Provider value={{ open, setOpen, triggerRef, menuId }}>
      <div className="relative">{children}</div>
    </MenuContext.Provider>
  );
}

export function DropdownMenuTrigger({
  className,
  children,
  ...props
}: ButtonHTMLAttributes<HTMLButtonElement>) {
  const menu = useMenu();
  return (
    <button
      ref={menu.triggerRef}
      type="button"
      className={cn(className)}
      aria-haspopup="menu"
      aria-expanded={menu.open}
      aria-controls={menu.menuId}
      onClick={() => menu.setOpen(!menu.open)}
      {...props}
    >
      {children}
    </button>
  );
}

export function DropdownMenuContent({
  className,
  align = 'start',
  children,
}: {
  className?: string;
  align?: 'start' | 'end';
  children: ReactNode;
}) {
  const menu = useMenu();
  const portal = useAdminPortalElement();
  const contentRef = useRef<HTMLDivElement>(null);
  const [box, setBox] = useState<{ top: number; left: number } | null>(null);

  const { open, setOpen, triggerRef } = menu;

  useEffect(() => {
    if (!open) return;
    const trigger = triggerRef.current;
    if (!trigger) return;
    const place = () => {
      const rect = trigger.getBoundingClientRect();
      const width = contentRef.current?.offsetWidth ?? 180;
      const left = align === 'end' ? rect.right - width : rect.left;
      setBox({ top: rect.bottom + 4, left: Math.max(8, left) });
    };
    place();
    const onPointerDown = (event: PointerEvent) => {
      const target = event.target as Node;
      if (contentRef.current?.contains(target) || trigger.contains(target)) return;
      setOpen(false);
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };
    window.addEventListener('resize', place);
    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      window.removeEventListener('resize', place);
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [align, open, setOpen, triggerRef]);

  if (!open) return null;

  const node = (
    <div
      ref={contentRef}
      id={menu.menuId}
      role="menu"
      style={box ? { position: 'fixed', top: box.top, left: box.left } : { position: 'fixed', visibility: 'hidden' }}
      className={cn(
        'z-50 min-w-[11rem] rounded-lg border border-nova-border bg-nova-popover p-1 text-nova-popover-foreground shadow-md',
        className
      )}
      onClick={() => setOpen(false)}
    >
      {children}
    </div>
  );

  if (portal) return createPortal(node, portal);
  return node;
}

export function DropdownMenuItem({
  className,
  onSelect,
  ...props
}: ButtonHTMLAttributes<HTMLButtonElement> & { onSelect?: () => void }) {
  const menu = useMenu();
  return (
    <button
      type="button"
      role="menuitem"
      className={cn(
        'flex h-8 w-full items-center rounded-md px-2 text-left text-sm text-nova-foreground hover:bg-nova-accent',
        className
      )}
      onClick={(event) => {
        props.onClick?.(event);
        onSelect?.();
        menu.setOpen(false);
      }}
      {...props}
    />
  );
}

export function DropdownMenuLabel({ className, ...props }: HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={cn('px-2 py-1 text-xs font-medium text-nova-muted-foreground', className)}
      {...props}
    />
  );
}

export function DropdownMenuSeparator() {
  return <div role="separator" className="-mx-1 my-1 h-px bg-nova-border" />;
}
